import React from 'react'

import { Props } from 'component-library/content/Picture/Picture'

import { classNames } from 'common/emotion'
import { StyledPicture } from 'component-library/content/Picture/Picture.styled'
import { ImageWidth } from 'common/helpers/image'
import { scaleImage } from 'common/helpers/scaleImage'
import { FillContainerHorizontallyPicture } from 'component-library/content/Picture/layouts/fill-container-horizontally'
import { ObjectFitContainPicture } from 'component-library/content/Picture/layouts/object-fit-contain'
import { ObjectFitCoverPicture } from 'component-library/content/Picture/layouts/object-fit-cover'
import { PictureWithNoStyles } from 'component-library/content/Picture/layouts/none'
import { ImageSrcInfo } from './layouts/helpers/srcSet'
import { fixedRatioToBreakpointRatios, getPlaceholderRatio } from './utils'
import { useProduct } from 'common/hooks/useProduct'

export const WebPicture: React.FC<Props> = props => {
    const product = useProduct()
    const imageWidth = props.imageWidths ?? 320
    const { className, fixedRatio, image, imageSizes } = props

    const renderPicture = props.renderPicture
        ? props.renderPicture
        : (pic: React.ReactElement) => pic

    if (props.layout === 'placeholder') {
        if (fixedRatio === 'original') {
            // We cannot render a placeholder without image dimensions
            // or fixed ratio
            return null
        }

        const placeHolderRatio = fixedRatio
            ? getPlaceholderRatio({}, fixedRatioToBreakpointRatios(fixedRatio))
            : undefined

        return renderPicture(
            <StyledPicture
                placeholderBreakpointRatios={placeHolderRatio}
                className={classNames(className, 'image')}
                loading
                loadingBackgroundColor={props.loadingBackgroundColor}
            />
        )
    }

    if (!image) {
        return null
    }

    const getScaledSrc = (src: string, width: ImageWidth) => scaleImage(src, width, false)

    const scaledImage: ImageSrcInfo = {
        product,
        srcCallback: getScaledSrc,
        initialWidth: imageWidth,
    }

    if (props.layout === 'fill-container-horizontally') {
        return (
            <FillContainerHorizontallyPicture
                className={props.className}
                image={image}
                srcInfo={scaledImage}
                imageSizes={imageSizes}
                fixedRatio={props.fixedRatio}
                disableLazyLoad={props.disableLazyLoad}
                renderPicture={renderPicture}
            />
        )
    }

    if (props.layout === 'object-fit-contain') {
        return (
            <ObjectFitContainPicture
                className={props.className}
                image={image}
                srcInfo={scaledImage}
                imageSizes={imageSizes}
                fixedRatio={props.fixedRatio}
                useOriginalCrop={props.useOriginalCrop || false}
                disableLazyLoad={props.disableLazyLoad}
                renderPicture={renderPicture}
            />
        )
    }

    if (props.layout === 'object-fit-cover') {
        return (
            <ObjectFitCoverPicture
                className={props.className}
                image={image}
                srcInfo={scaledImage}
                imageSizes={imageSizes}
                fixedRatio={props.fixedRatio}
                useOriginalCrop={props.useOriginalCrop || false}
                disableLazyLoad={props.disableLazyLoad}
                renderPicture={renderPicture}
            />
        )
    }

    if (props.layout === 'none') {
        return (
            <PictureWithNoStyles
                className={props.className}
                image={image}
                srcInfo={scaledImage}
                imageSizes={imageSizes}
                fixedRatio={props.fixedRatio}
                disableLazyLoad={props.disableLazyLoad}
                renderPicture={renderPicture}
            />
        )
    }

    return null
}

WebPicture.displayName = 'WebPicture'
